import * as Yup from 'yup'
import cronstrue from 'cronstrue'
import moment from 'moment'

/**
 * The schedules and pools lists shouldn't use numerical index keys, and the name can be changed.
 * It's just easiest to generate a random key for them on initial load and addition.
 */
export const randKey = () =>
  Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

/**
 * Normalize a keyed-hash into an array of its keys.
 *
 * @param data null|Object
 */
export const keysAsArray = (data) => (data ? Object.keys(data) : [])

const DEPLOYMENT_RESOURCE_FIELDS = [
  'worker_memory_mb',
  'commander_memory_mb',
  'worker_cpu_request_mi_cpu',
  'commander_cpu_request_mi_cpu',
  'worker_cpu_max_mi_cpu',
  'commander_cpu_max_mi_cpu',
] as const

const normalizeDeploymentResourceFields = (config: Record<string, any>) =>
  DEPLOYMENT_RESOURCE_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field]: config[field] === '' || config[field] == null ? null : Number(config[field]),
    }),
    {}
  )

/**
 * Transforms portions of API-formatted data to a structure Formik can handle.
 * List-like keyed hashes turn into arrays, eg.:
 *    `{ key: { ...data }, key2: { ...data2 } }` transforms to `[ { name: key, ...data }, { name: key2, ...data2 } ]`
 *
 * For V2 configs, schedules are a list of adapter groups.
 * For V1 configs (no version field), individual schedules are converted to the form structure in 'schedules'.
 */
export const normalizeToFormikState = (config: TranslatorConfig) => {
  let {
    prototype: isPrototype,
    translator_config,
    adapter_module,
    adapter_version,
    ...rest
  } = config

  // Determine if this is a V2 config - only use explicit version field
  // V1 configs will not have a version field, V2+ will be explicitly versioned
  const isV2 = translator_config.version === '2'

  let schedules = []

  if (isV2) {
    // V2: Schedules is a list of adapter groups
    schedules = ((translator_config.schedules as ScheduleGroup[]) || []).map(
      (sg: ScheduleGroup) => ({
        key: randKey(), // Add a key for Formik FieldArray
        adapter: sg.adapter,
        type: sg.type,
        adapter_config: sg.adapter_config,
        schedules: (sg.schedules || []).map((individualSchedule) => ({
          key: randKey(), // Add a key for Formik FieldArray
          interval_minutes: individualSchedule.interval_minutes,
          cron: individualSchedule.cron,
          priority: individualSchedule.priority,
          start_date: individualSchedule.start_date,
          start_days_ago: individualSchedule.start_days_ago,
          is_scrubber: individualSchedule.is_scrubber,
        })),
      })
    )
  } else {
    // V1: Individual schedules (no version field)
    schedules = keysAsArray(translator_config.schedules).map((groupName) => {
      const schedule = (translator_config.schedules as { [key: string]: Schedule })[groupName]
      return {
        key: randKey(),
        name: groupName,
        ...schedule,
      }
    })
  }

  return {
    prototype: isPrototype,

    global: translator_config.global,

    operations: translator_config.operations,

    adapter: {
      adapter_module: adapter_module,
      adapter_version: adapter_version,
    },

    auth: translator_config.auth,

    consul_services: keysAsArray(translator_config.consul_services).map((key) => ({
      key,
      value: translator_config.consul_services[key],
    })),

    schedules, // V1 schedules or V2 list of adapter groups

    pools: keysAsArray(translator_config.pools).map((key) => ({
      key: randKey(),
      name: key,
      ...translator_config.pools[key],
      jdbc_info_enabled: translator_config.pools[key].jdbc_info != null,
    })),

    version: translator_config.version || '1',

    ...rest,
  }
}

/**
 * Transforms Formik data back into structure the API accepts.
 * Handles both V1 (flat schedules) and V2 (list of adapter groups) formats.
 * Only V1 configs will not have a version field. V2+ will be explicitly versioned.
 *
 * @param args Object
 */
export const normalizeToApiBody = ({
  prototype,
  adapter,
  schedules,
  pools,
  auth,
  global,
  operations,
  consul_services,
  version,
  ...rest
}) => {
  // Use explicit version field only. V1 configs have no version field, V2+ are explicitly versioned
  const configVersion = version || '1'
  const isV2 = configVersion === '2'

  let translatorConfigPayload: any = {
    version: configVersion,
    created_at: new Date().toISOString(),
    auth: {
      adapter: auth.adapter,
      adapter_config: auth.adapter_config,
    },
    global,
    operations,
    consul_services: consul_services.reduce(
      (acc, { key, value }: any) => ({ ...acc, [key]: value }),
      {}
    ),
    pools: pools.reduce((acc, { name, jdbc_info_enabled, ...pool }) => {
      const cleanedPool = {
        ...pool,
        jdbc_info: jdbc_info_enabled ? pool.jdbc_info : null,
      }
      return { ...acc, [name]: cleanedPool }
    }, {}),
  }

  if (isV2) {
    translatorConfigPayload.schedules = (schedules as ScheduleGroup[]).map((sg) => ({
      adapter: sg.adapter,
      type: sg.type,
      adapter_config: sg.adapter_config,
      schedules: sg.schedules.map((individualSchedule) => ({
        priority:
          (individualSchedule.priority as any) !== '' && individualSchedule.priority != null
            ? Number(individualSchedule.priority)
            : undefined,
        is_scrubber: individualSchedule.is_scrubber,
        ...(individualSchedule.interval_minutes && {
          interval_minutes: Number(individualSchedule.interval_minutes),
        }),
        ...(individualSchedule.cron && { cron: individualSchedule.cron }),
        ...(individualSchedule.start_date && {
          start_date: moment(individualSchedule.start_date).isValid()
            ? moment.utc(individualSchedule.start_date).format('YYYY-MM-DDTHH:mm:ss') + 'Z'
            : individualSchedule.start_date,
        }),
        ...(individualSchedule.start_days_ago && {
          start_days_ago: String(individualSchedule.start_days_ago),
        }),
      })),
    }))
  } else {
    // V1: Flat Schedules
    translatorConfigPayload.schedules = schedules.reduce((acc, schedule) => {
      const { name, key: _key, ...scheduleData } = schedule
      acc[name] = scheduleData
      return acc
    }, {})
  }

  return {
    prototype,

    adapter_module: adapter.adapter_module,

    adapter_version: adapter.adapter_version,

    translator_config: translatorConfigPayload,

    ...rest,

    ...normalizeDeploymentResourceFields(rest),
  }
}

/**
 * When there is no existing config for an environment, this is used for the Formik initial values.
 */
export const emptyConfig: TranslatorConfig = {
  translator_id: '',
  company_slug: '',
  environment: '',
  adapter_module: '',
  adapter_version: '',
  prototype: false,
  worker_memory_mb: null,
  commander_memory_mb: null,
  worker_cpu_request_mi_cpu: null,
  commander_cpu_request_mi_cpu: null,
  worker_cpu_max_mi_cpu: null,
  commander_cpu_max_mi_cpu: null,
  single_deployment: true,
  user: null,
  created_at: null,
  deleted_at: null,
  revision_comment: null,
  translator_config: {
    version: '2', // Default new configs to V2
    translator_name: '',
    company_slug: '',
    created_at: '',
    consul_services: {},
    auth: {},
    pools: {},
    schedules: [], // V2 is an array of groups
    global: {},
    operations: {},
  } as TranslatorConfigConfig,
}

// @todo: some of these may benefit from having common defaults?
export const newSchedule = {
  name: '',
  adapter: '',
  type: '',
  adapter_config: null,
  priority: '',
  start_days_ago: '',
  interval_minutes: '',
  cron: '',
}

// New schedule group for V2 configs
export const newScheduleGroup = {
  adapter: '',
  type: 'Bulk', // Default to a valid type
  adapter_config: null,
  schedules: [],
}

// Individual schedule within a V2 schedule group
export const newIndividualSchedule = {
  interval_minutes: '',
  cron: '',
  priority: '',
  start_date: '',
  start_days_ago: '',
  is_scrubber: false,
}

// @todo: some of these may benefit from having common defaults?
export const newPool = {
  name: '',
  max_connections: '',
  timeout: '',
  rate_limit_count: '',
  rate_limit_seconds: '',
  jdbc_info_enabled: false,
  jdbc_info: {
    connection_url: '',
    connection_address: '',
    connection_port: '',
    consul_service_name: '',
    user_name: '',
    password: '',
    fully_qualified_driver_name: '',
    legacy_driver: false,
  },
}

/**
 * Inputs only allow strings (not null or undef).
 * Yup.number type-checking will fail an empty string as NaN.
 * To allow an empty value, transform it to undef first.
 */
const emptyNumberTransform = (currentValue: any, origValue: any) =>
  origValue === '' ? undefined : currentValue

/**
 * Validates CRON expressions via cRonstrue lib.
 * An exception is thrown for invalid expressions.
 *
 * https://github.com/bradymholt/cRonstrue
 *
 * @param value string
 */
const cronValidator = (value) => {
  if (!value) return true

  try {
    cronstrue.toString(value)
  } catch (err) {
    return false
  }

  return true
}

/**
 * Validates that the value is empty.
 * Used when one of two properties should be filled, but not both.
 *
 * @param value any
 */
const emptyValidator = (value) => {
  return !value
}

export const cronExplained = (value) => {
  if (!value) return 'No CRON expression'

  try {
    return cronstrue.toString(value)
  } catch {
    return 'Invalid CRON expression'
  }
}

/**
 * Helper to check if a schedule is a duplicate within its adapter group.
 * Schedules are duplicates if they have the same:
 * - scrubber setting (is_scrubber)
 * - start time (start_date OR start_days_ago)
 *
 * @param schedule The schedule to check
 * @param scheduleIndex The index of the schedule in the group
 * @param allSchedulesInGroup All schedules in the same adapter group
 */
export const checkDuplicateSchedule = (
  schedule: any,
  scheduleIndex: number,
  allSchedulesInGroup: any[]
): boolean => {
  const getScheduleSignature = (sched: any) => {
    const isScrubber = sched.is_scrubber || false
    const startTime = sched.start_date || sched.start_days_ago || null
    return `${isScrubber}|${startTime}`
  }

  const currentSignature = getScheduleSignature(schedule)

  return allSchedulesInGroup.some((sched, idx) => {
    if (idx === scheduleIndex) return false // Skip self
    return getScheduleSignature(sched) === currentSignature
  })
}

/**
 * Yup validation test for checking if a schedule is a duplicate within the same adapter group.
 */
const isDuplicateSchedule = function (this: Yup.TestContext) {
  const schedule = this.parent

  // Get the schedule index from the path (e.g., "schedules[0].schedules[2].priority" -> 2)
  const pathParts = (this.path || '').split('.')

  // Find the inner schedules array index (the individual schedule)
  let scheduleIndex: number | undefined
  let groupIndex: number | undefined

  // Parse path like "schedules[0].schedules[2].priority"
  for (const part of pathParts) {
    const match = part.match(/schedules\[(\d+)\]/)
    if (match) {
      if (groupIndex === undefined) {
        groupIndex = Number.parseInt(match[1], 10)
      } else {
        scheduleIndex = Number.parseInt(match[1], 10)
        break
      }
    }
  }

  if (groupIndex === undefined || scheduleIndex === undefined) return true

  // Access the form values - try to get from root using this.options.parent
  const rootValues = this.options.context || (this as any).from?.[0]?.value || {}
  const allSchedulesInGroup = rootValues?.schedules?.[groupIndex]?.schedules || []

  return !checkDuplicateSchedule(schedule, scheduleIndex, allSchedulesInGroup)
}

export const ScheduleSchema = Yup.object().shape({
  name: Yup.string()
    .required('Required')
    .matches(/^[\w-]+$/, 'May only contain alphanumeric _ and - characters'),

  adapter: Yup.string().required('Required'),

  type: Yup.string().required('Required'),

  priority: Yup.number().typeError('Must be a number').required('Required'),

  start_days_ago: Yup.number().typeError('Must be a number').nullable().notRequired(),

  start_date: Yup.string().typeError('Must be a date').nullable().notRequired(),

  interval_minutes: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  cron: Yup.string()
    .nullable()
    .when('interval_minutes', ([val], schema) =>
      val === '' || val === null || val === undefined
        ? schema
            .required('Either interval minutes or cron is required')
            .test('valid-cron', 'Invalid CRON expression', cronValidator)
        : schema
            .nullable()
            .test('empty', 'You can not specify both interval minutes and cron.', emptyValidator)
    ),
})

// V2 Individual Schedule Schema (within a group)
export const IndividualScheduleSchema = Yup.object().shape({
  priority: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform)
    .test(
      'unique-schedule',
      'Duplicate schedule detected: A schedule with the same scrubber setting and start time already exists in this adapter group',
      isDuplicateSchedule
    ),

  start_days_ago: Yup.number().typeError('Must be a number').nullable().notRequired(),

  start_date: Yup.string()
    .nullable()
    .notRequired()
    .test('is-valid-date', 'Must be a valid date (YYYY-MM-DD or ISO format)', (value) => {
      if (!value) return true // Nullable, so allow empty
      const isIsoString = value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})$/)
      if (isIsoString) {
        return moment(value).isValid() // Validate as general ISO string
      }
      return moment(value, 'YYYY-MM-DD', true).isValid() // Strictly validate as YYYY-MM-DD
    }),

  is_scrubber: Yup.boolean().nullable().notRequired(),

  interval_minutes: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  cron: Yup.string()
    .nullable()
    .when('interval_minutes', ([val], schema) =>
      val === '' || val === null || val === undefined
        ? schema
            .required('Either interval minutes or cron is required')
            .test('valid-cron', 'Invalid CRON expression', cronValidator)
        : schema
            .nullable()
            .test('empty', 'You can not specify both interval minutes and cron.', emptyValidator)
    ),
})

// V2 Schedule Group Schema (now AdapterConfigSchema is the name)
export const AdapterConfigSchema = Yup.object().shape({
  adapter: Yup.string().required('Adapter is required'),

  type: Yup.string().required('Type is required'),

  adapter_config: Yup.object().nullable().notRequired(),

  schedules: Yup.array()
    .of(IndividualScheduleSchema)
    .min(0, 'At least one schedule is recommended')
    .nullable()
    .notRequired(),
})

export const PoolSchema = Yup.object().shape({
  name: Yup.string()
    .required('Required')
    .matches(/^[\w-]+$/, 'May only contain alphanumeric _ and - characters'),

  max_connections: Yup.number().typeError('Must be a number').required('Required'),

  timeout: Yup.number().typeError('Must be a number').required('Required'),

  rate_limit_count: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  rate_limit_seconds: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  jdbc_info_enabled: Yup.boolean(),

  jdbc_info: Yup.object()
    .nullable()
    .notRequired()
    .when('jdbc_info_enabled', ([enabled], schema) =>
      enabled
        ? schema.shape({
            connection_url: Yup.string().required('Required'),

            user_name: Yup.string().required('Required'),

            password: Yup.string().required('Required'),

            connection_address: Yup.string().nullable().notRequired(),

            connection_port: Yup.number()
              .typeError('Must be a number')
              .nullable()
              .notRequired()
              .transform(emptyNumberTransform)
              .when('connection_address', ([val], schema) =>
                emptyValidator(val)
                  ? schema
                      .nullable()
                      .notRequired()
                      .test(
                        'empty',
                        'If connection_address is not set, connection_port must not be set',
                        emptyValidator
                      )
                  : schema
                      .required('If connection_address is set, connection_port must be set')
                      .transform(emptyNumberTransform)
              ),

            consul_service_name: Yup.string()
              .nullable()
              .when(['connection_address', 'connection_port'], ([address, port], schema) =>
                emptyValidator(address) && emptyValidator(port)
                  ? schema.required(
                      'Either [connection_address and connection_port] or consul_service_name is required'
                    )
                  : schema
                      .nullable()
                      .test(
                        'empty',
                        'You cannot specify both connection_address/port and consul_service_name',
                        emptyValidator
                      )
              ),
            legacy_driver: Yup.boolean().nullable().notRequired(),

            fully_qualified_driver_name: Yup.string().nullable().notRequired(),
          })
        : schema.shape({
            connection_url: Yup.string().nullable().notRequired(),

            user_name: Yup.string().nullable().notRequired(),

            password: Yup.string().nullable().notRequired(),

            connection_address: Yup.string().nullable().notRequired(),

            connection_port: Yup.number()
              .typeError('Must be a number')
              .nullable()
              .notRequired()
              .transform(emptyNumberTransform),

            consul_service_name: Yup.string().nullable().notRequired(),

            legacy_driver: Yup.boolean().nullable().notRequired(),

            fully_qualified_driver_name: Yup.string().nullable().notRequired(),
          })
    ),
})

export const TranslatorConfigSchema = Yup.object().shape({
  new_revision_comment: Yup.string().required('Required').max(1024),

  worker_memory_mb: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  commander_memory_mb: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  worker_cpu_request_mi_cpu: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  commander_cpu_request_mi_cpu: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  worker_cpu_max_mi_cpu: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  commander_cpu_max_mi_cpu: Yup.number()
    .typeError('Must be a number')
    .nullable()
    .notRequired()
    .transform(emptyNumberTransform),

  single_deployment: Yup.boolean(),

  adapter: Yup.object().shape({
    adapter_module: Yup.string().required('Required'),

    adapter_version: Yup.string().required('Required'),
  }),

  auth: Yup.object().shape({
    adapter: Yup.string().required('Required'),

    adapter_config: Yup.object().nullable(),
  }),

  global: Yup.object().nullable(),

  operations: Yup.object().nullable(),

  consul_services: Yup.array(Yup.object()).nullable(),

  // Conditional validation based on version
  schedules: Yup.array().when('version', ([version], schema) => {
    return version === '2'
      ? schema
          .of(AdapterConfigSchema)
          .test('unique-schedules', 'Duplicate schedules found', (groups: any, context) => {
            if (!groups) return true
            const keys = new Set()
            for (const group of groups) {
              const { adapter, type, schedules } = group
              if (!schedules) continue
              for (const schedule of schedules) {
                // Generate key based on fields that define uniqueness in V2
                const start = schedule.start_date || schedule.start_days_ago || ''
                const key = `${adapter}:${type}:${schedule.is_scrubber}:${start}`

                if (keys.has(key)) {
                  return context.createError({
                    message: `Duplicate schedule detected (Adapter: ${adapter}, Type: ${type}, Scrubber: ${schedule.is_scrubber ? 'Yes' : 'No'}, Start: ${start || 'None'}). Schedules must be unique.`,
                  })
                }
                keys.add(key)
              }
            }
            return true
          })
      : schema.of(ScheduleSchema) // Validation for V1 map as flat list
  }),

  pools: Yup.array().of(PoolSchema),

  version: Yup.string().nullable().notRequired(),
})
